import React from 'react';
import { Container } from 'reactstrap';
import Layout from '../../templates/Layout';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import {
  CREATE_EMOC,
  GET_ALL_EMOCS,
  GET_EMOC_EMOC_BY_MOC_NUMBER,
  LINK_EMOC_JOBS,
  REMOVE_EMOC,
  UPDATE_EMOC,
} from '../../../graph-queries/tar/emoc';
import { TEmoc } from '../../../lib/types';
import AspectsTable from '../../organisms/AspectsTable/AspectsTable';
import EmocForm from '../../organisms/EmocForm/EmocForm';
import { useSelectedAsset } from '../../../lib/util';
import { useHasTarAdminAccess, useHasUserAccess } from '../../../lib/util/auth';
import { ColumnDef } from '@tanstack/react-table';
import { authorCell, useAlertText } from '../../organisms/AspectsTable/util';
import FormModal from '../../molecules/FormModal/FormModal';

export const columns: ColumnDef<TEmoc>[] = [
  {
    header: 'Plan Number',
    accessorKey: 'mocNumber',
    enableSorting: false,
    cell: (props) => {
      const row = props.row.original;
      return (
        <a href={row.emocEmoc?.uRL} target="_blank" rel="noreferrer">
          {row.mocNumber}
        </a>
      );
    },
  },
  {
    header: 'MOC Description',
    accessorKey: 'emocEmoc.mocDescription',
    enableSorting: false,
  },
  {
    header: 'Initiator',
    accessorKey: 'emocEmoc.mocInitiator',
    enableSorting: false,
  },
  {
    header: 'Status',
    accessorKey: 'emocEmoc.mocStatus',
    enableSorting: false,
  },
  {
    header: 'Comments',
    accessorKey: 'comments',
    enableSorting: false,
  },
  {
    header: 'Last Updated',
    accessorKey: 'updatedAt',
    cell: authorCell,
  },
];

const EmocsPage: React.FC = () => {
  const { alertText, setAlertText } = useAlertText();
  const hasUserAccess = useHasUserAccess();
  const hasTarAdminAccess = useHasTarAdminAccess();
  const asset = useSelectedAsset();
  const client = useApolloClient();

  const { data, loading } = useQuery(GET_ALL_EMOCS, {
    variables: { assetId: asset?.assetId || -1 },
    skip: !asset?.assetId,
    onCompleted: (data) => {
      // Maintain cache for edit modal
      data.allEmocs.forEach((emoc) => {
        if (emoc.emocEmoc)
          client.writeQuery({
            query: GET_EMOC_EMOC_BY_MOC_NUMBER,
            data: { emocEmoc: emoc.emocEmoc },
            variables: { mocNumber: emoc.mocNumber, assetId: emoc.assetId },
          });
      });
    },
  });

  const [createEmoc, { loading: isCreating }] = useMutation(CREATE_EMOC, {
    refetchQueries: [GET_ALL_EMOCS],
    awaitRefetchQueries: true,
  });
  const [updateEmoc, { loading: isUpdating }] = useMutation(UPDATE_EMOC, {
    refetchQueries: [GET_ALL_EMOCS],
    awaitRefetchQueries: true,
  });
  const [linkEmocJobs, { loading: isLinking }] = useMutation(LINK_EMOC_JOBS, {
    refetchQueries: [GET_ALL_EMOCS],
    awaitRefetchQueries: true,
  });
  const [removeEmoc, { loading: isRemoving }] = useMutation(REMOVE_EMOC, {
    refetchQueries: [GET_ALL_EMOCS],
    awaitRefetchQueries: true,
  });

  const onAddSubmit = (emoc: TEmoc) => {
    const { mocId, updatedAt, updatedBy, ...input } = emoc;
    return createEmoc({
      variables: { input },
      onCompleted: () => setAlertText('MOC successfully added'),
    });
  };
  const onEditSubmit = (emoc: TEmoc) => {
    const { updatedAt, updatedBy, ...input } = emoc;
    return updateEmoc({
      variables: { input },
      onCompleted: () => setAlertText('MOC successfully updated'),
    });
  };

  const onLinkSubmit = (selection: TEmoc[], jobIds: number[]) => {
    return Promise.all(
      selection.map((emoc) =>
        linkEmocJobs({
          variables: {
            mocId: emoc.mocId,
            jobIds: jobIds,
          },
        })
      )
    ).then(() =>
      setAlertText(`MOC successfully linked to ${jobIds.length} job${jobIds.length > 1 ? 's' : ''}`)
    );
  };

  const onRemoveSubmit = (selection: TEmoc[]) => {
    return Promise.all(
      selection.map((emoc) =>
        removeEmoc({
          variables: { mocId: emoc.mocId },
        })
      )
    ).then(() => setAlertText(`MOC successfully deleted`));
  };

  const isLoading = isCreating || isUpdating || isLinking || isRemoving || loading || !!alertText;
  return (
    <Layout>
      <Container>
        {loading ? (
          'Loading...'
        ) : (
          <AspectsTable<TEmoc>
            hideSelectionColumn
            aspectName="MOC"
            data={(data?.allEmocs as unknown as TEmoc[]) || []}
            onLinkSubmit={onLinkSubmit}
            onRemoveSubmit={onRemoveSubmit}
            columns={columns}
            alertText={alertText}
            renderAddForm={(onClose) => (
              <EmocForm onSubmit={onAddSubmit} onClose={onClose} type={'add'} />
            )}
            renderEditForm={(onClose, defaultValue) => (
              <EmocForm
                defaultValues={defaultValue}
                onSubmit={onEditSubmit}
                onClose={onClose}
                type={'edit'}
                isLoading={isLoading}
              />
            )}
            Modal={FormModal}
            creationDisabled={!hasUserAccess}
            getRowMutationDisabled={(_row) => !hasUserAccess}
            getRowDeletionDisabled={(_row) => !hasTarAdminAccess}
            searchEnabled={true}
            searchPlaceholderText="Search the emoc tracker..."
            isLoading={isLoading}
          />
        )}
      </Container>
    </Layout>
  );
};

export default EmocsPage;
